import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'

import { ConfirmationResultModal, Alert, Checkbox, Spacer } from '@percent/lemonade'
import { useMutation, APIErrorHandler, API_ERROR_MESSAGE } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { RedactAccountModalProps } from './RedactAccountModal.types'

export function RedactAccountModal({ isOpen, onClose, onSuccess, account }: RedactAccountModalProps) {
  const { t } = useTranslation()
  const { accountService } = useServices()
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const memberDisplay = account.fullName ? `${account.fullName} (${account.email})` : account.email

  const [{ status, isPending }, { mutateAsync: redactAccount }] = useMutation(accountService.redactAccount)

  const state = status === 'success' ? 'success' : status === 'error' ? 'failure' : undefined

  const handleConfirm = async () => {
    setErrorMessage(undefined)
    try {
      await redactAccount(account)
      onSuccess?.()
    } catch (err) {
      const axiosError = err as AxiosError<{ error: Record<string, unknown> }>
      const apiError = APIErrorHandler(axiosError?.response?.data?.error)
      setErrorMessage(
        apiError === API_ERROR_MESSAGE.genericError
          ? t('account.modify.redact.failure.description', { member: memberDisplay })
          : apiError
      )
    }
  }

  const handleClose = () => {
    if (!isPending) {
      setIsConfirmed(false)
      setErrorMessage(undefined)
      onClose()
    }
  }

  return (
    <ConfirmationResultModal
      success={{
        title: t('account.modify.redact.success'),
        description: t('account.modify.redact.success.description', { member: memberDisplay }),
        button: t('button.close')
      }}
      failure={{
        title: t('account.modify.redact.failure'),
        description: errorMessage ?? t('account.modify.redact.failure.description', { member: memberDisplay }),
        button: t('button.close')
      }}
      title={t('account.modify.redact.title')}
      primaryButtonText={t('account.modify.redact.confirmButton')}
      secondaryButtonText={t('button.cancel')}
      primaryBtnTestId="redact-account-submit-button"
      secondaryBtnTestId="redact-account-cancel-button"
      buttonTestId="redact-account-close-button"
      state={state}
      loading={isPending}
      disabled={!isConfirmed}
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <Alert variant="warning" title={t('account.modify.redact.warning')} />
      <Spacer size={8} axis="vertical" />
      <Checkbox
        name="redact-confirmation"
        label={t('account.modify.redact.checkbox', { member: memberDisplay })}
        variant="default"
        dataTestId="redact-account-checkbox"
        active={isConfirmed}
        necessity="required"
        onChange={e => setIsConfirmed((e.target as HTMLInputElement).checked)}
      />
    </ConfirmationResultModal>
  )
}
